import { z } from "zod";

/**
 * 店铺在后台地址与请求头里的表示：myshopify 子域名（完整店铺域名）。
 * 与 `apps/shopify` 的 Tenant.shopDomain 同一份格式，两边不各写正则。
 */
export const shopDomainSchema = z
  .string()
  .regex(/^[a-z0-9][a-z0-9-]*\.myshopify\.com$/);

export type ShopDomain = z.infer<typeof shopDomainSchema>;

/**
 * 后台每个 API 请求都带这个头，值是页面所属店铺的域名。
 * 它不授予访问权限；服务端要求它与 Shopify ID token 的店铺一致，
 * 或用它选择独立入口的每店 Cookie 并核对 Tenant。
 */
export const storeRequestHeader = "X-GoNovus-Store";

/** 同一商家后台构建在 Shopify 内的文档入口。 */
export const embeddedAdminPrefix = "/app/console";

/** 后台页面地址的店铺前缀：/stores/<店铺>/… */
export function storeBasePath(shopDomain: string): string {
  return `/stores/${shopDomain}`;
}

/**
 * 从独立或内嵌页面地址读出店铺域名；没有店铺前缀、或店铺段不合法时
 * 返回 null——调用方据此走「从 Shopify 打开」的提示而不是发请求。
 */
export function readStoreFromPathname(pathname: string): string | null {
  const storePath = pathname.startsWith(`${embeddedAdminPrefix}/`)
    ? pathname.slice(embeddedAdminPrefix.length)
    : pathname;
  const segment = /^\/stores\/([^/]+)/.exec(storePath)?.[1];
  if (segment === undefined) return null;
  let decoded: string;
  try {
    decoded = decodeURIComponent(segment);
  } catch {
    return null;
  }
  const parsed = shopDomainSchema.safeParse(decoded);
  return parsed.success ? parsed.data : null;
}
